import { useCallback, useMemo, useState } from 'react';
import {
    useToast,
} from '@chakra-ui/react';
import { FiSave, FiX } from 'react-icons/fi';
import { saveQuoteRevision } from '../../api/revision-api';
import { getQuoteDiff, getCellDiff, mapSnapshotToRows, mapCellSnapshotToRows, calculateTotalFromRows, TotalType, mapQuoteTableRowsToReadOnly, buildReadOnlyTotals, LEGACY_COLUMNS } from './utils';
import { QuoteTableEdit } from './QuoteTableEdit';
import type { CellEditValues } from './QuoteTableEdit';
import { ReadOnlyQuoteTable } from './components/ReadOnlyQuoteTable';
import type { QuoteRevisionSnapshot, SaveQuoteCellPayload } from '../../types/revision.type';
import type { RfxQuoteTable } from '../../types/quote.type';
import type { QuoteTableColumn } from '../../../components/QuoteTable/quote-table.types';
import { Modal } from 'newui/components/ui/Modal';
import { Button } from 'newui/components/Button';

export interface UpdateQuoteModalProps {
    currentData: QuoteRevisionSnapshot;
    inviteId: number;
    isOpen: boolean;
    previousData: QuoteRevisionSnapshot;
    quote: RfxQuoteTable;
    onClose: () => void;
    onSaveSuccess: (isQuoteTabValid?: boolean) => void;
}

interface EditableAnswerValues {
    [itemId: number]: {
        id?: number;
        quantity: string;
        rate: string;
    };
}

const buildInitialEditValues = (data: QuoteRevisionSnapshot): EditableAnswerValues => {
    const initial: EditableAnswerValues = {};
    if (!data?.answers) {
        return initial;
    }
    data.answers.forEach((ans) => {
        initial[ans.rfq_quote_item_id] = {
            id: ans.id,
            quantity: String(ans.answer.value.quantity),
            rate: String(ans.answer.value.rate),
        };
    });
    return initial;
};

const buildInitialCellEditValues = (data: QuoteRevisionSnapshot): CellEditValues => {
    const initial: CellEditValues = {};
    if (!data?.cells) {
        return initial;
    }
    data.cells.forEach((cell) => {
        if (!initial[cell.rfx_quote_row_id]) {
            initial[cell.rfx_quote_row_id] = {};
        }
        initial[cell.rfx_quote_row_id][cell.rfx_quote_column_id] = cell.value ?? '';
    });
    return initial;
};

const mapColumnsToTableColumns = (quote: RfxQuoteTable): QuoteTableColumn[] => {
    if (!quote.columns || quote.columns.length === 0) {
        return [];
    }
    return quote.columns.map((col) => ({
        id: col.id,
        columnType: col.columnType,
        label: col.name,
        sortOrder: col.sortOrder,
        isSupplierEditable: col.isSupplierEditable,
    }));
};

const UpdateQuoteModal: React.FC<UpdateQuoteModalProps> = ({
    isOpen,
    onClose,
    inviteId,
    currentData,
    previousData,
    onSaveSuccess,
    quote,
}) => {
    const toast = useToast();
    const [isLoading, setIsLoading] = useState(false);

    const hasColumns = Boolean(quote.columns && quote.columns.length > 0);
    const tableColumns = useMemo(() => mapColumnsToTableColumns(quote), [quote]);

    // Legacy answer-based state
    const [editValues, setEditValues] = useState<EditableAnswerValues>(
        () => buildInitialEditValues(currentData)
    );

    // Normalised cell-based state
    const [editCellValues, setEditCellValues] = useState<CellEditValues>(
        () => buildInitialCellEditValues(currentData)
    );

    // ── Legacy path computations ──────────────────────────────

    const currentWithEdits = useMemo((): QuoteRevisionSnapshot => {
        if (hasColumns) {
            return currentData;
        }
        return {
            ...currentData,
            answers: (currentData?.answers ?? []).map((ans) => ({
                ...ans,
                answer: {
                    value: {
                        ...ans.answer.value,
                        quantity: editValues[ans.rfq_quote_item_id]?.quantity ?? ans.answer.value.quantity,
                        rate: editValues[ans.rfq_quote_item_id]?.rate ?? ans.answer.value.rate,
                    },
                },
            })),
        };
    }, [currentData, editValues, hasColumns]);

    const legacyDiffKeys = useMemo(
        () => (!hasColumns && currentData && previousData ? getQuoteDiff(currentWithEdits, previousData) : new Set<string>()),
        [currentWithEdits, previousData, currentData, hasColumns]
    );

    // ── Cell path computations ────────────────────────────────

    const cellDiffKeys = useMemo(() => {
        if (!hasColumns) {
            return new Set<string>();
        }
        return getCellDiff(editCellValues, buildInitialCellEditValues(previousData));
    }, [hasColumns, editCellValues, previousData]);

    const diffKeys = hasColumns ? cellDiffKeys : legacyDiffKeys;

    const currentRows = useMemo(() => {
        if (hasColumns) {
            return mapCellSnapshotToRows(editCellValues, quote.rows);
        }
        return mapSnapshotToRows(currentWithEdits, quote.rows);
    }, [hasColumns, editCellValues, currentWithEdits, quote.rows]);

    const previousRows = useMemo(() => {
        if (hasColumns) {
            return mapCellSnapshotToRows(buildInitialCellEditValues(previousData), quote.rows);
        }
        return mapSnapshotToRows(previousData, quote.rows);
    }, [hasColumns, previousData, quote.rows]);

    const previousSubtotal = useMemo(
        () => calculateTotalFromRows(previousRows, TotalType.SubTotal),
        [previousRows]
    );

    // Convert previous rows for ReadOnlyQuoteTable
    const readOnlyColumns = hasColumns ? tableColumns : LEGACY_COLUMNS;

    const readOnlyPreviousRows = useMemo(
        () => mapQuoteTableRowsToReadOnly(previousRows, readOnlyColumns),
        [previousRows, readOnlyColumns]
    );

    const previousTotals = useMemo(() => {
        if (hasColumns) {
            return undefined;
        }
        return buildReadOnlyTotals(previousSubtotal).filter((t) => t.id === TotalType.SubTotal);
    }, [hasColumns, previousSubtotal]);

    const hasChanges = diffKeys.size > 0;

    // ── Handlers ──────────────────────────────────────────────

    const handleValueChange = useCallback(
        (itemId: number, field: 'quantity' | 'rate', value: string) => {
            setEditValues((prev) => ({
                ...prev,
                [itemId]: {
                    ...prev[itemId],
                    [field]: value,
                },
            }));
        },
        []
    );

    const handleCellValueChange = useCallback(
        (rowId: number, columnId: number, value: string) => {
            setEditCellValues((prev) => ({
                ...prev,
                [rowId]: {
                    ...prev[rowId],
                    [columnId]: value,
                },
            }));
        },
        []
    );

    const handleSave = async () => {
        setIsLoading(true);
        try {
            let result;
            if (hasColumns) {
                // Build cell payload
                const cells: SaveQuoteCellPayload[] = [];
                Object.entries(editCellValues).forEach(([rowId, columnMap]) => {
                    Object.entries(columnMap).forEach(([columnId, val]) => {
                        cells.push({
                            row_id: Number(rowId),
                            column_id: Number(columnId),
                            value: String(val) || null,
                        });
                    });
                });
                result = await saveQuoteRevision(
                    { inviteId: inviteId, quoteId: quote.id },
                    { cells }
                );
            } else {
                // Legacy payload
                const quoteAnswers: Record<string, unknown> = {};
                Object.entries(editValues).forEach(([itemId, vals]) => {
                    quoteAnswers[itemId] = {
                        ...(vals.id != null && { id: vals.id }),
                        quantity: Number(vals.quantity),
                        rate: Number(vals.rate),
                    };
                });
                result = await saveQuoteRevision(
                    { inviteId: inviteId, quoteId: quote.id },
                    { quote_answers: quoteAnswers }
                );
            }
            toast({
                title: 'Revision saved',
                description: 'Your quote revision has been saved.',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
            onSaveSuccess(result.isQuoteTabValid);
            onClose();
        } catch {
            toast({
                title: 'Error',
                description: 'Failed to save quote revision.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setEditValues(buildInitialEditValues(currentData));
        setEditCellValues(buildInitialCellEditValues(currentData));
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleCancel}
            size="full"
            title="Update Quote"
            modalType='standard'
            iconType='edit'
            customFooter={<div className='flex justify-end gap-4'>
                <Button
                    btntype='secondary'
                    onClick={handleCancel}
                    disabled={isLoading}
                    data-testid="cancel-update-btn"
                >
                    <FiX /> <span className='pl-2'>Cancel Changes</span>
                </Button>
                <Button
                    onClick={handleSave}
                    isLoading={isLoading}
                    disabled={!hasChanges || isLoading}
                    data-testid="save-revision-btn"
                >
                    <FiSave /> <span className='pl-2'>Save Revision</span>
                </Button></div>}
        >
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="mb-6">
                    <p className="mb-3 text-base font-semibold text-gray-700">
                        Current Revision (Editable) - {currentData.label}
                    </p>
                    <QuoteTableEdit
                        rows={currentRows}
                        editValues={editValues}
                        onValueChange={handleValueChange}
                        columns={hasColumns ? tableColumns : undefined}
                        editCellValues={hasColumns ? editCellValues : undefined}
                        onCellValueChange={hasColumns ? handleCellValueChange : undefined}
                        diffKeys={diffKeys}
                        previousSubtotal={previousSubtotal}
                        showUnitColumn={!hasColumns}
                        showSubTotal={!hasColumns}
                    />
                </div>

                <div>
                    <p className="mb-3 text-base font-semibold text-gray-500">
                        Previous Revision (Read-only) - {previousData.label}
                    </p>
                    <ReadOnlyQuoteTable
                        columns={readOnlyColumns}
                        rows={readOnlyPreviousRows}
                        totals={previousTotals}
                        muted
                    />
                </div>
            </div>
        </Modal>
    );
};

export { UpdateQuoteModal };
